import React, { forwardRef } from "react";
import { buildRobustNervousSystemProfile } from "@/components/assessment/robustAssessmentEngine";
import { buildRobustClientNarrative } from "@/components/assessment/robustResultNarrative";

const colors = {
  ink: "#172221",
  soft: "#4F5E5C",
  mute: "#77817F",
  canvas: "#FBF9F4",
  warm: "#F3EEE2",
  white: "#FFFFFF",
  line: "#E5E1D6",
  teal: "#2E716A",
  tealSoft: "#DDECE9",
};

const PAGE_COUNT = 11;

const pdfText = (value) =>
  String(value || "").replace(/[\u2010-\u2015]/g, " - ");

const Page = ({ number, title, children, cover = false }) => (
  <section
    data-robust-pdf-page
    data-pdf-page-number={number}
    style={{
      width: 794,
      height: 1123,
      boxSizing: "border-box",
      padding: "46px 54px 38px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      background: colors.canvas,
      color: colors.ink,
    }}
  >
    {!cover && (
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: 12,
          borderBottom: `1px solid ${colors.line}`,
          color: colors.mute,
          fontSize: 9,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <span>APOS nervous-system profile</span>
        <span>{pdfText(title)}</span>
      </header>
    )}
    <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    <footer
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingTop: 12,
        borderTop: `1px solid ${colors.line}`,
        color: colors.mute,
        fontSize: 9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
      }}
    >
      <span>FORR Health - Adaptive Physiology</span>
      <span>{number} / {PAGE_COUNT}</span>
    </footer>
  </section>
);

const Eyebrow = ({ children }) => (
  <div
    style={{
      color: colors.teal,
      fontSize: 9.5,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      marginBottom: 9,
    }}
  >
    {pdfText(children)}
  </div>
);

const Heading = ({ children, size = 28 }) => (
  <h2
    style={{
      margin: 0,
      color: colors.ink,
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontSize: size,
      lineHeight: 1.18,
      fontWeight: 500,
      letterSpacing: "-0.015em",
    }}
  >
    {pdfText(children)}
  </h2>
);

const Paragraph = ({ children, size = 12, color = colors.soft, marginTop = 12 }) => (
  <p
    style={{
      margin: `${marginTop}px 0 0`,
      color,
      fontSize: size,
      lineHeight: 1.58,
      fontWeight: 300,
    }}
  >
    {pdfText(children)}
  </p>
);

const Card = ({ title, eyebrow, children, tint = false }) => (
  <div
    style={{
      padding: 17,
      borderRadius: 14,
      border: `1px solid ${colors.line}`,
      background: tint ? colors.warm : colors.white,
    }}
  >
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    {title && (
      <div
        style={{
          color: colors.ink,
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 16,
          lineHeight: 1.25,
        }}
      >
        {pdfText(title)}
      </div>
    )}
    {children}
  </div>
);

const Bullets = ({ items, size = 10.5 }) => (
  <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none" }}>
    {(items || []).map((item) => (
      <li
        key={item}
        style={{
          marginBottom: 6,
          color: colors.soft,
          fontSize: size,
          lineHeight: 1.42,
          fontWeight: 300,
        }}
      >
        <span style={{ color: colors.teal }}>- </span>
        {pdfText(item)}
      </li>
    ))}
  </ul>
);

const SignalPills = ({ items }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 10 }}>
    {(items || []).map((item) => (
      <span
        key={item}
        style={{
          padding: "6px 10px",
          borderRadius: 999,
          background: colors.tealSoft,
          color: colors.ink,
          fontSize: 9.5,
          lineHeight: 1.2,
        }}
      >
        {pdfText(item)}
      </span>
    ))}
  </div>
);

const Systems = ({ items }) => (
  <div style={{ marginTop: 7 }}>
    {(items || []).map((item, index) => (
      <div key={item.name} style={{ marginTop: index ? 8 : 0 }}>
        <div
          style={{
            color: colors.ink,
            fontSize: 9.8,
            lineHeight: 1.35,
            fontWeight: 600,
          }}
        >
          {pdfText(item.name)}
        </div>
        <div
          style={{
            marginTop: 2,
            color: colors.soft,
            fontSize: 9.2,
            lineHeight: 1.4,
            fontWeight: 300,
          }}
        >
          {pdfText(item.role)}
        </div>
      </div>
    ))}
  </div>
);

const DailyLifeOverviewPage = ({ number, dailyLife }) => (
  <Page number={number} title="How this may show up">
    <div style={{ paddingTop: 28 }}>
      <Eyebrow>How this may show up in daily life</Eyebrow>
      <Heading>
        Emotion, body, thinking, and behaviour are parts of one response
      </Heading>
      <div style={{ marginTop: 18 }}>
        <Card eyebrow="What this combination may mean" tint>
          {dailyLife.overview.map((paragraph) => (
            <Paragraph
              key={paragraph}
              marginTop={10}
              size={10.8}
              color={colors.ink}
            >
              {paragraph}
            </Paragraph>
          ))}
        </Card>
      </div>
      <div style={{ marginTop: 14 }}>
        <Card eyebrow="Why change is still possible">
          <Paragraph marginTop={0} size={10.4} color={colors.ink}>
            {dailyLife.plasticitySnippet}
          </Paragraph>
        </Card>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1.08fr 0.92fr", gap: 13, marginTop: 14 }}>
        <Card eyebrow="A note on vagal language">
          <Paragraph marginTop={0} size={9.4}>
            {dailyLife.vagalClarifier}
          </Paragraph>
        </Card>
        <Card eyebrow="Research starting points" tint>
          <Bullets items={dailyLife.researchStartingPoints} size={9.1} />
        </Card>
      </div>
      <div style={{ marginTop: 17 }}>
        <Eyebrow>Research references</Eyebrow>
        <Bullets items={dailyLife.sources} size={8.8} />
      </div>
    </div>
  </Page>
);

const DailyLifePage = ({ number, section, index }) => (
  <Page number={number} title={section.title}>
    <div style={{ paddingTop: 28 }}>
      <Eyebrow>Daily-life pattern {index + 1} of 4</Eyebrow>
      <Heading>{section.title}</Heading>
      <Paragraph size={11.5} color={colors.ink}>
        {section.summary}
      </Paragraph>
      <div style={{ marginTop: 15 }}>
        <Card eyebrow="Signals that may fit" tint>
          <SignalPills items={section.signals} />
        </Card>
      </div>
      <div style={{ marginTop: 13 }}>
        <Card eyebrow="Why this may happen">
          <Paragraph marginTop={0} size={10.3} color={colors.ink}>
            {section.explanation}
          </Paragraph>
        </Card>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginTop: 13 }}>
        <Card eyebrow="Systems involved" tint>
          <Systems items={section.systems} />
        </Card>
        <Card eyebrow="Questions worth asking yourself" tint>
          <Bullets items={section.questions} size={9.2} />
        </Card>
      </div>
      <div style={{ marginTop: 13 }}>
        <Card eyebrow="What could help in these situations">
          <Bullets items={section.supports} size={9.5} />
        </Card>
      </div>
      {section.safetyNote && (
        <Paragraph marginTop={10} size={8.8} color={colors.mute}>
          {section.safetyNote}
        </Paragraph>
      )}
    </div>
  </Page>
);

const SciencePage = ({ number, module }) => (
  <Page number={number} title={module.id === "bdnf" ? "BDNF" : "Neuroplasticity"}>
    <div style={{ paddingTop: 30 }}>
      <Eyebrow>Your nervous system can adapt</Eyebrow>
      <Heading>{module.title}</Heading>
      <div style={{ marginTop: 18 }}>
        <Card eyebrow="What it means" tint>
          <Paragraph marginTop={0} color={colors.ink}>{module.science}</Paragraph>
          <Paragraph>{module.biology}</Paragraph>
        </Card>
      </div>
      <div style={{ height: 14 }} />
      <Card eyebrow="Why this matters for your pattern">
        <Paragraph marginTop={0} color={colors.ink}>{module.profileRelevance}</Paragraph>
        <Paragraph>{module.homeostasis}</Paragraph>
      </Card>
      <div style={{ height: 14 }} />
      <Card eyebrow="Bottom line" tint>
        <Paragraph marginTop={0} color={colors.ink}>{module.bottomLine}</Paragraph>
      </Card>
      <div style={{ marginTop: 22 }}>
        <Eyebrow>Science references</Eyebrow>
        <Bullets items={module.sources} size={9.5} />
      </div>
    </div>
  </Page>
);

const RobustAssessmentPDF = forwardRef(function RobustAssessmentPDF(
  { answers, profile: suppliedProfile, date = new Date() },
  ref,
) {
  const profile = suppliedProfile || buildRobustNervousSystemProfile(answers);
  const narrative = buildRobustClientNarrative(profile);
  const completed = new Date(date);
  const dateLabel = Number.isNaN(completed.getTime())
    ? "Assessment profile"
    : completed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
  const [neuroplasticity, bdnf] = narrative.scienceModules;

  return (
    <div ref={ref} data-testid="robust-assessment-pdf">
      <Page number={1} title="Your profile" cover>
        <div style={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center" }}>
          <Eyebrow>APOS - Adaptive Physiology assessment</Eyebrow>
          <h1
            style={{
              margin: 0,
              maxWidth: 650,
              color: colors.ink,
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 43,
              lineHeight: 1.09,
              fontWeight: 500,
              letterSpacing: "-0.025em",
            }}
          >
            {pdfText(profile.headline)}
          </h1>
          <div
            style={{
              marginTop: 32,
              padding: 24,
              borderRadius: 18,
              background: colors.tealSoft,
              border: `1px solid ${colors.line}`,
            }}
          >
            <Eyebrow>What your responses suggest</Eyebrow>
            <Heading size={24}>{narrative.stateName}</Heading>
            <Paragraph color={colors.ink}>{narrative.stateDescription}</Paragraph>
            <Paragraph>{profile.bottomLine}</Paragraph>
          </div>
          <div style={{ marginTop: 24, fontSize: 11, color: colors.mute }}>
            {pdfText(dateLabel)}
          </div>
          <Paragraph size={10.5}>
            This is not a label for who you are. It is a working explanation of how
            your nervous system may organize demand, choice, emotion, and recovery.
          </Paragraph>
        </div>
      </Page>

      <Page number={2} title="How your nervous system may be working">
        <div style={{ paddingTop: 28 }}>
          <Eyebrow>A look inside your response</Eyebrow>
          <Heading>How your nervous system may be working</Heading>
          <Paragraph>
            Your response is not one score. These four parts interact and help
            explain why the same person may respond differently across situations.
          </Paragraph>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginTop: 22 }}>
            {narrative.nervousSystemStory.map((item, index) => (
              <Card key={item.key} eyebrow={`0${index + 1}`} title={item.title}>
                <Paragraph marginTop={9} size={10.5}>{item.body}</Paragraph>
              </Card>
            ))}
          </div>
          <div style={{ marginTop: 18 }}>
            <Card eyebrow="What your nervous system may be prioritizing" tint>
              <Bullets items={narrative.priorities} />
            </Card>
          </div>
        </div>
      </Page>

      <Page number={3} title="Why this pattern may be happening">
        <div style={{ paddingTop: 28 }}>
          <Eyebrow>Why your system may be functioning this way</Eyebrow>
          <Heading>The pattern may be protective, learned, and context-dependent</Heading>
          <div style={{ marginTop: 18 }}>
            <Card tint>
              {narrative.whyThisMayBeHappening.map((paragraph) => (
                <Paragraph key={paragraph} marginTop={10} size={10.7} color={colors.ink}>
                  {paragraph}
                </Paragraph>
              ))}
            </Card>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginTop: 14 }}>
            <Card eyebrow="What may be adding demand">
              <Bullets items={narrative.reportedDemand.slice(0, 6)} size={9.5} />
            </Card>
            <Card eyebrow="What may already support you">
              <Bullets items={narrative.reportedSupport.slice(0, 6)} size={9.5} />
            </Card>
          </div>
        </div>
      </Page>

      <DailyLifeOverviewPage number={4} dailyLife={narrative.dailyLife} />
      {narrative.dailyLife.sections.map((section, index) => (
        <DailyLifePage
          key={section.key}
          number={5 + index}
          section={section}
          index={index}
        />
      ))}

      <SciencePage number={9} module={neuroplasticity} />
      <SciencePage number={10} module={bdnf} />

      <Page number={11} title="Your starting direction">
        <div style={{ paddingTop: 28 }}>
          <Eyebrow>Your starting direction</Eyebrow>
          <Heading>{profile.baselineInput.title}</Heading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginTop: 20 }}>
            <Card eyebrow="What your nervous system may need" tint>
              <Paragraph marginTop={0} size={10.7} color={colors.ink}>
                {profile.baselineInput.direction}
              </Paragraph>
            </Card>
            <Card eyebrow="How to begin" tint>
              <Paragraph marginTop={0} size={10.7} color={colors.ink}>
                {profile.baselineInput.dose}
              </Paragraph>
            </Card>
          </div>
          <div style={{ marginTop: 16 }}>
            <Card eyebrow="Priorities ranked by likely leverage">
              {profile.priorities.map((priority, index) => (
                <div key={priority.title} style={{ marginTop: index ? 12 : 0 }}>
                  <div style={{ color: colors.ink, fontSize: 11.5, fontWeight: 600 }}>
                    {index + 1}. {pdfText(priority.title)}
                  </div>
                  <Paragraph marginTop={3} size={9.8}>{priority.body}</Paragraph>
                </div>
              ))}
            </Card>
          </div>
          <div style={{ marginTop: 14 }}>
            <Card eyebrow="Keep what already works" tint>
              <Bullets items={profile.noChangeNeeded} size={9.7} />
            </Card>
          </div>
          <div style={{ marginTop: 14 }}>
            <Card eyebrow="Continue with APOS Companion">
              <Paragraph marginTop={0} size={10.2}>
                APOS keeps the technical baseline in the background. Current
                check-ins, scheduled demands, completed activities, and
                before-to-after responses help it learn what may be useful for you
                under comparable conditions.
              </Paragraph>
            </Card>
          </div>
        </div>
      </Page>
    </div>
  );
});

export default RobustAssessmentPDF;
